import { useState, useRef, useEffect } from 'react'
import { useTheme } from '../context/ThemeContext'

// Floating in-app help assistant (web + installed app).
// FREE, fully client-side: guided FAQ with tappable topic chips. Click a chip
// for an instant how-to, then related chips appear. Free-text also works.

// id, label (chip text), keywords (for typed search), answer, related (chip ids).
const FAQ = [
  { id: 'schedule', label: '📅 Schedule a meeting',
    k: ['schedule', 'meeting', 'book', 'create meeting', 'new meeting'],
    a: 'To schedule a meeting:\n1. Open Calendar (or Meetings).\n2. In Month view, tap a day to select it; in Week/Day view, tap an hour slot to select that time.\n3. Tap "+ Schedule" — the date and time are pre-filled.\n4. Pick the employee(s), add a title, and save.',
    related: ['calendar', 'review', 'job'] },
  { id: 'calendar', label: '🗓️ Calendar views',
    k: ['calendar', 'month', 'week', 'day view', 'views'],
    a: 'The Calendar shows a Month view on the app, and Month / Week / Day views on the web. Tapping a day or hour slot just selects it (highlights it). Then tap "+ Schedule" to create a meeting at that time.',
    related: ['schedule'] },
  { id: 'expense', label: '🧾 Submit an expense',
    k: ['expense', 'reimburse', 'receipt', 'claim', 'spend'],
    a: 'To submit an expense:\n1. Open Expense Log.\n2. Enter the amount, category, and a date (it can\'t be in the future).\n3. Upload the receipt (there\'s a size limit).\n4. Submit. Your employer approves it — or rejects it with a reason you\'ll see.',
    related: ['approve', 'job'] },
  { id: 'approve', label: '✅ Approve or reject',
    k: ['reject', 'rejected', 'approve', 'approval'],
    a: 'Employers review submissions: open the item (expense, job, or PDR), then approve it or reject it with a reason. Whoever submitted it will see the rejection reason.',
    related: ['expense', 'pdr', 'job'] },
  { id: 'job', label: '🛠️ Jobs & tasks',
    k: ['job', 'task', 'assign', 'assigned', 'request'],
    a: 'Jobs:\n• Employers: open Job Requests, create a job with a title, due date (not in the past), and optional attachment, then assign it.\n• Employees: open Assigned Jobs, tap a job to see details, add notes, attach files, and move it through its status.',
    related: ['approve', 'expense'] },
  { id: 'pdr', label: '🎯 Set up a PDR',
    k: ['pdr', 'development', 'personal development', 'goal', 'goals'],
    a: 'PDRs (Personal Development Reviews):\n1. Employees create a PDR and set goals / a development plan (the goal box is wide for detail).\n2. Submit it. Employers review it under "PDR Approvals" and approve it.',
    related: ['review', 'approve'] },
  { id: 'review', label: '⭐ Reviews & self-score',
    k: ['review', 'performance', 'self score', 'self-score', 'evidence', 'rating'],
    a: 'Reviews:\n1. Open Reviews.\n2. Add your self-score and, if asked, attach an evidence file.\n3. Submit. Employers can see your self-score and complete the review.',
    related: ['pdr', 'checkin'] },
  { id: 'checkin', label: '🟢 Check in / out',
    k: ['check in', 'check-in', 'checkin', 'checkout', 'check out', 'attendance', 'clock'],
    a: 'Use Check-in / Checkout to mark your attendance for the day. Employers can see the whole team\'s check-in status.',
    related: ['team', 'review'] },
  { id: 'notif', label: '🔔 Notifications & sound',
    k: ['notification', 'bell', 'sound', 'alert', 'notify'],
    a: 'Notifications appear under the bell icon at the top, with a sound. To turn the sound on or off, open notification settings from the bell.',
    related: ['password'] },
  { id: 'password', label: '🔑 Change password',
    k: ['password', 'change password', 'reset password', 'forgot'],
    a: 'To change your password, open your account settings and use "Change password". If you\'re locked out, ask your admin to reset it.',
    related: ['notif'] },
  { id: 'invite', label: '➕ Invite an employee',
    k: ['invite', 'add employee', 'add user', 'team member', 'new employee'],
    a: 'Employers can add people via "Invite Employee" in the sidebar. Share the invite with your team member so they can sign in.',
    related: ['team', 'access'] },
  { id: 'access', label: '🎟️ Signup access code',
    k: ['access code', 'signup', 'sign up', 'register', 'create account', 'create organization', 'new org'],
    a: 'Creating a new organization (employer signup) needs a signup access code. Employees join an existing organization through their employer\'s invite.',
    related: ['invite'] },
  { id: 'install', label: '📲 Install the app',
    k: ['install', 'app', 'home screen', 'download', 'android', 'phone', 'mobile'],
    a: 'To install the app: open the site in Chrome, open the menu (⋮), and choose "Add to Home screen". Updates apply automatically the next time you open it — no reinstalling needed.',
    related: ['notif'] },
  { id: 'team', label: '👥 View my team',
    k: ['team', 'employees', 'staff', 'see my team'],
    a: 'Employers can open "Team" to view their employees, their profiles, and their self-scores.',
    related: ['invite', 'checkin'] },
  { id: 'billing', label: '💳 Billing & plans',
    k: ['billing', 'plan', 'plans', 'payment', 'subscription'],
    a: 'Employers can open "Billing & Plans" in the sidebar for plan details.',
    related: ['invite'] },
]

const byId = Object.fromEntries(FAQ.map((e) => [e.id, e]))
const STARTERS = ['schedule', 'expense', 'pdr', 'job', 'review', 'install']
const GREETINGS = ['hi', 'hey', 'hello', 'yo', 'help', 'thanks', 'thank you']

function matchEntry(question) {
  const q = question.toLowerCase()
  let best = null, bestScore = 0
  for (const entry of FAQ) {
    let score = 0
    for (const kw of entry.k) if (q.includes(kw)) score += kw.includes(' ') ? 2 : 1
    if (score > bestScore) { bestScore = score; best = entry }
  }
  return bestScore > 0 ? best : null
}

export default function HelpAssistant() {
  const { t } = useTheme()
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [chips, setChips] = useState(STARTERS)
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm your Employee Hive helper. Tap a topic below, or type a question." },
  ])
  const scrollRef = useRef(null)

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages, open, chips])

  function reply(userText, entry) {
    if (entry) {
      setMessages((m) => [...m, { role: 'user', content: userText }, { role: 'assistant', content: entry.a }])
      // Show related chips, falling back to a few other starters.
      const rel = (entry.related || []).filter((id) => id !== entry.id)
      const fill = STARTERS.filter((id) => id !== entry.id && !rel.includes(id))
      setChips([...rel, ...fill].slice(0, 5))
    } else {
      setMessages((m) => [...m, { role: 'user', content: userText }, {
        role: 'assistant',
        content: "I'm not sure about that one yet. Pick a topic below, or ask your admin for anything beyond these.",
      }])
      setChips(STARTERS)
    }
  }

  function onChip(id) {
    const entry = byId[id]
    if (entry) reply(entry.label.replace(/^[^\w]+\s*/, ''), entry)
  }

  function send() {
    const text = input.trim()
    if (!text) return
    setInput('')
    const q = text.toLowerCase()
    if (GREETINGS.some((g) => q === g || q.startsWith(g + ' '))) {
      setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: 'Hi! Tap a topic below or ask me how to do something.' }])
      setChips(STARTERS)
      return
    }
    reply(text, matchEntry(text))
  }

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }

  const accent = '#4F46E5'
  const panelBg = t?.surface || '#FFFFFF'
  const border = t?.border || '#E2E8F0'
  const text = t?.text || '#0F172A'
  const chipBg = t?.dark ? '#0F172A' : '#EEF2FF'

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open help"
          style={{
            position: 'fixed', right: 18, bottom: 18, zIndex: 60,
            width: 56, height: 56, borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: `linear-gradient(135deg,${accent},#6366F1)`, color: '#fff',
            fontSize: 24, boxShadow: '0 8px 24px rgba(79,70,229,0.45)',
          }}
        >💬</button>
      )}

      {open && (
        <div
          className="eh-help-panel"
          style={{
            position: 'fixed', right: 18, bottom: 18, zIndex: 60,
            width: 360, maxWidth: 'calc(100vw - 24px)', height: 540, maxHeight: 'calc(100vh - 36px)',
            display: 'flex', flexDirection: 'column',
            background: panelBg, border: `1px solid ${border}`, borderRadius: 16,
            boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: `linear-gradient(135deg,${accent},#6366F1)`, color: '#fff' }}>
            <span style={{ fontSize: 20 }}>🐝</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Hive Helper</div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>Tap a topic or ask a question</div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                <div style={{
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13.5, lineHeight: 1.5,
                  padding: '9px 12px', borderRadius: 12,
                  background: m.role === 'user' ? accent : (t?.dark ? '#0F172A' : '#F1F5F9'),
                  color: m.role === 'user' ? '#fff' : text,
                  border: m.role === 'user' ? 'none' : `1px solid ${border}`,
                }}>{m.content}</div>
              </div>
            ))}

            {/* Topic chips */}
            {chips.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 2 }}>
                {chips.map((id) => byId[id] && (
                  <button key={id} onClick={() => onChip(id)} style={{
                    fontSize: 12.5, fontWeight: 600, padding: '7px 11px', borderRadius: 999,
                    cursor: 'pointer', background: chipBg, color: accent,
                    border: `1px solid ${accent}33`, fontFamily: 'inherit',
                  }}>{byId[id].label}</button>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, padding: 10, borderTop: `1px solid ${border}` }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder="Ask a question…"
              style={{
                flex: 1, resize: 'none', maxHeight: 90, padding: '9px 11px', fontSize: 13.5,
                borderRadius: 10, border: `1px solid ${border}`, outline: 'none',
                background: t?.input || '#fff', color: t?.inputText || text, fontFamily: 'inherit',
              }}
            />
            <button
              onClick={send}
              disabled={!input.trim()}
              style={{
                alignSelf: 'flex-end', padding: '9px 14px', borderRadius: 10, border: 'none',
                background: !input.trim() ? '#9CA3AF' : accent, color: '#fff',
                fontWeight: 600, fontSize: 13, cursor: !input.trim() ? 'default' : 'pointer',
              }}
            >Send</button>
          </div>
        </div>
      )}
    </>
  )
}
